/**
 * Landing Page V2 - Riders Corner Section
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  fontPrimary,
  typography,
  borders,
} from '@/design-system/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useRidersCorner } from '@/hooks/useExperiences';
import type { Experience } from '@/services/experienceService';
import { getExperienceImage } from '@/services/experienceService';
import { ScrollReveal } from './ScrollReveal';

// Fallback rides if API fails (not used - sections hide if no API data)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackRides: Experience[] = [
  {
    _id: 'ride-1',
    name: 'Delhi to Jaisalmer',
    title: 'Delhi to Jaisalmer – Desert Roads & Royal Vibes',
    shortTitle: 'Desert Roads Ride',
    slug: 'delhi-jaisalmer',
    code: 'RIDE-001',
    type: 'riders_corner',
    category: {
      title: 'Adventure',
      description: 'Desert ride',
      _id: 'cat-ride-1',
    },
    location: {
      _id: 'loc-ride-1',
      name: 'Jaisalmer',
      state: 'Rajasthan',
      city: 'Jaisalmer',
    },
    imageUrl: ['/assets/images/location/image-5.png'],
    description:
      'Embark on an unforgettable journey from Delhi to Jaisalmer, riding through desert landscapes.',
    price: 8000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 65,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'ride-2',
    name: 'Chennai to Pondicherry',
    title: 'Chennai to Pondicherry – Scenic Shores',
    shortTitle: 'Coastal Ride',
    slug: 'chennai-pondicherry',
    code: 'RIDE-002',
    type: 'riders_corner',
    category: {
      title: 'Coastal',
      description: 'Coastal ride',
      _id: 'cat-ride-2',
    },
    location: {
      _id: 'loc-ride-2',
      name: 'Pondicherry',
      state: 'Puducherry',
      city: 'Pondicherry',
    },
    imageUrl: ['/assets/images/location/image-6.png'],
    description:
      'Embark on an amazing journey from Chennai to Pondicherry! Enjoy the stunning coastal views.',
    price: 5000,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 72,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
];

export function RidersCornerSection() {
  // Fetch riders corner using React Query
  const { data, isLoading, error } = useRidersCorner();

  // Ref for scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);
  const shouldPreventClickRef = React.useRef(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== RidersCorner Debug ===');
    console.log('isLoading:', isLoading);
    console.log('data:', data);
    console.log('data length:', data?.length);
    console.log('error:', error);
    console.log('=============================');
  }, [data, isLoading, error]);

  // Only use API data, don't show fallback
  // Hide section if no API data
  const hasApiData = data && Array.isArray(data) && data.length > 0;
  const rides = hasApiData ? data : [];

  // Enable mouse drag scrolling
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if container has horizontal overflow
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) {
        return;
      }

      // Allow dragging even on links, but we'll prevent navigation if dragged
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const rect = container.getBoundingClientRect();
      startXRef.current = e.clientX - rect.left;
      scrollLeftRef.current = container.scrollLeft;

      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      // Prevent default to avoid text selection and link navigation
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const walk = x - startXRef.current; // Distance moved

      // Only mark as dragged if movement is significant (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
        shouldPreventClickRef.current = true;
      }

      // Update scroll position
      // Dragging right (positive walk) should scroll right (increase scrollLeft)
      // Dragging left (negative walk) should scroll left (decrease scrollLeft)
      const newScrollLeft = scrollLeftRef.current - walk;
      container.scrollLeft = newScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;

      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }

      // Reset hasDragged after a short delay to allow click handler to check it
      if (hasDraggedRef.current) {
        // Keep the flag for a bit to prevent click
        setTimeout(() => {
          hasDraggedRef.current = false;
          shouldPreventClickRef.current = false;
        }, 200);
      } else {
        // No drag occurred, allow click
        shouldPreventClickRef.current = false;
        hasDraggedRef.current = false;
      }
    };

    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      hasDraggedRef.current = false;
      shouldPreventClickRef.current = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Prevent navigation if we dragged
      if (shouldPreventClickRef.current || hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    // Use capture phase to intercept clicks before they reach links
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isLoading, rides.length]);

  console.log('RidersCorner - Final rides count:', rides.length);
  console.log(
    'RidersCorner - Using:',
    data && data.length > 0 ? 'API DATA' : 'FALLBACK'
  );

  // Hide section if no API data
  if (!hasApiData && !isLoading) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      className='px-4 py-8 md:px-20 md:py-16'
      style={{}}
      direction='up'
      amount={0.25}
      margin='0px 0px -120px 0px'
    >
      <ScrollReveal
        className='max-w-[1394px] mx-auto'
        delay={0.1}
        direction='none'
      >
        {/* Header */}
        <ScrollReveal
          className='flex flex-col items-center mb-8 w-full md:w-[744px] mx-auto'
          style={{
            gap: spacing[2], // 8px gap between header elements
          }}
          delay={0.2}
          direction='up'
        >
          <div
            className='flex items-center justify-center shrink-0'
            style={{
              gap: '10px',
              paddingLeft: spacing[1], // 4px
              paddingRight: spacing[3], // 12px
              paddingTop: spacing[1], // 4px
              paddingBottom: spacing[1], // 4px
            }}
          >
            <div
              className='flex items-center justify-center shrink-0 border border-black/12'
              style={{
                backgroundColor: '#fde047', // Yellow-300 from Figma
                padding: spacing[2], // 8px
                borderRadius: borders.radius.full,
                width: '36px',
                height: '36px',
              }}
            >
              <Image
                src='/assets/images/icons/landing-page/riders_corner.svg'
                alt='Riders Corner'
                width={20}
                height={20}
                unoptimized
              />
            </div>
            <p
              className='shrink-0'
              style={{
                fontFamily: fontPrimary,
                fontWeight: typography.weight.bold,
                fontSize: typography.size.base, // 16px
                lineHeight: 'normal',
                color: typographyColors.primary,
              }}
            >
              Riders Corner
            </p>
          </div>
          <h2
            className='
    text-center whitespace-pre-wrap font-semibold
    text-[24px] leading-[32px]
    md:text-[48px] md:leading-[52px]
  '
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.semibold, // 600
              color: typographyColors.primary,
            }}
          >
            Built for those who live to ride.
          </h2>

          <p
            className='text-sm md:text-xl font-normal text-center whitespace-pre-wrap'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.regular,
              lineHeight: 'normal',
              color: typographyColors.primary,
            }}
          >
            Join our vibrant community of passionate riders and free spirits.
          </p>
        </ScrollReveal>

        {/* Ride Cards - Fixed 2-card layout */}
        <div
          style={{
            marginTop: spacing[8], // 32px gap from header
            marginBottom: spacing[8], // 32px gap to explore link
          }}
        >
          <div
            className='flex flex-col md:flex-row'
            style={{
              gap: '18px', // 18px gap between cards from Figma
              width: '100%',
            }}
          >
            {isLoading ? (
              // Loading skeleton - show only 2 cards
              <>
                {[1, 2].map(i => (
                  <div
                    key={i}
                    className='flex-1 animate-pulse rounded-xl overflow-hidden'
                    style={{
                      minWidth: '280px',
                      height: '257px',
                      backgroundColor: backgroundColors.secondary,
                    }}
                  />
                ))}
              </>
            ) : (
              rides.slice(0, 2).map(ride => (
                <div
                  key={ride._id}
                  className='flex-1'
                  style={{
                    minWidth: '280px',
                  }}
                >
                  <RideCard experience={ride} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Explore Link */}
        <ScrollReveal
          className='flex items-center justify-center'
          style={{ gap: spacing[1.5] }} // 6px
          delay={0.45}
          direction='none'
        >
          <Link
            href='/riders-corner'
            className='cursor-pointer hover:opacity-80 transition-opacity underline'
            style={{
              fontFamily: fontPrimary,
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: 'normal',
              color: typographyColors.primary,
              textUnderlinePosition: 'from-font',
              textDecorationLine: 'underline',
            }}
          >
            Explore All Curations
          </Link>
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
              clipRule='evenodd'
              fill='#27272a'
            />
          </svg>
        </ScrollReveal>
      </ScrollReveal>
    </ScrollReveal>
  );
}

interface RideCardProps {
  experience: Experience;
}

function RideCard({ experience }: RideCardProps) {
  const {
    _id,
    title,
    description,
    category,
    shortTitle,
    name,
    location,
    price,
    currencySymbol,
  } = experience;

  // Get image from media array, with fallback to imageUrl
  const displayImage = getExperienceImage(experience);

  // Use shortTitle if available, otherwise title or name
  const displayTitle = shortTitle || title || name;

  // Use description or create default
  const displayDescription =
    description ||
    `Explore the beautiful landscapes of ${location?.name || 'this destination'}`;

  // Use category title
  const displayCategory = category?.title || 'Ride';

  // Distance and duration - these should ideally come from API
  const distance = '780 kms'; // Default value
  const duration = '12 - 14 hrs'; // Default value

  // Format price
  const displayPrice = price
    ? `${currencySymbol || '₹'}${price.toLocaleString()}+`
    : '';

  return (
    <Link href={`/riders-corner/${_id}`}>
      <div
        className='flex flex-col md:flex-row w-full bg-white cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0'
        style={{
          borderRadius: '12px',
          border: '1px solid #e7e5e4',
          boxShadow: '0px 16px 30px rgba(25, 24, 19, 0.06)',
          padding: '12px 16px 12px 12px',
          width: '100%',
          gap: '16px',
          alignSelf: 'start',
          overflow: 'hidden',
        }}
      >
        {/* Image */}
        {displayImage && (
          <div className='relative w-full md:w-[213px] h-[121px] md:h-auto rounded-[8px] overflow-hidden flex-shrink-0 md:self-stretch'>
            <Image
              src={displayImage}
              alt={displayTitle}
              fill
              className='object-cover'
            />
          </div>
        )}

        {/* Content */}
        <div
          className='flex-1 flex flex-col'
          style={{
            minHeight: 0,
            overflow: 'hidden',
            paddingTop: spacing[4], // 16px
            paddingBottom: 0,
            gap: spacing[6], // 24px
          }}
        >
          <div
            style={{
              flexShrink: 0,
              gap: spacing[4],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
            }}
            className='flex flex-col'
          >
            {/* Category Badge */}
            <div className='flex items-center h-[21px]'>
              <span
                className='inline-flex items-center justify-center font-medium text-sm rounded-[4px]'
                style={{
                  backgroundColor: '#f6ebb5',
                  border: '1px solid #fde047',
                  color: typographyColors.primary,
                  padding: '2px 8px',
                  gap: '2px',
                }}
              >
                {displayCategory}
              </span>
            </div>

            {/* Title and Description */}
            <div style={{ gap: '6px' }} className='flex flex-col'>
              <h3
                className='text-2xl font-bold line-clamp-2'
                style={{
                  color: typographyColors.primary,
                  margin: 0,
                  lineHeight: 'normal',
                  // minHeight: 'calc(1.5rem * 1.2 * 2)', // text-2xl is 1.5rem, line-height 1.2, 2 lines
                }}
              >
                {displayTitle}
              </h3>
              <p
                className='text-base line-clamp-2'
                style={{
                  color: typographyColors.secondary,
                  margin: 0,
                  fontWeight: 300,
                  lineHeight: '1.5',
                  minHeight: 'calc(1rem * 1.5 * 2)', // text-base is 1rem, line-height 1.5, 2 lines
                }}
              >
                {displayDescription}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div
            className='flex items-center'
            style={{ flexShrink: 0, gap: spacing[2] }}
          >
            <Pill
              iconSrc='/assets/images/icons/motorcycle_vector.svg'
              label={distance}
              isClock={false}
            />
            <Pill
              iconSrc='/assets/images/icons/clock-outline.svg'
              label={duration}
              isClock={true}
            />
            {displayPrice && (
              <div
                style={{
                  backgroundColor: '#fef3c7',
                  borderColor: '#fde68a',
                }}
                className='border flex items-center gap-1 px-2 py-1 rounded-md'
              >
                <span
                  className='text-sm font-semibold'
                  style={{ color: '#7c2d12' }}
                >
                  {displayPrice}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

interface PillProps {
  iconSrc: string;
  label: string;
  isClock?: boolean;
}

function Pill({ iconSrc, label, isClock = false }: PillProps) {
  const commonPillStyle = {
    display: 'flex',
    padding: '4px 8px 4px 4px',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#f5f5f4',
    border: '1px solid #e7e5e4',
    borderRadius: '6px',
    flexShrink: 0,
  };

  if (isClock) {
    // Clock pill has different structure per Figma
    return (
      <div style={commonPillStyle} className='flex'>
        <div
          className='flex flex-col items-center justify-center'
          style={{
            backgroundColor: '#e7e5e4',
            borderRadius: '2px',
            padding: '3px 4px',
            gap: '8px',
            flexShrink: 0,
            width: '26.333px',
            height: '19.333px',
            minWidth: '26.333px',
          }}
        >
          <div
            className='relative'
            style={{
              width: '16px',
              height: '16px',
            }}
          >
            <Image src={iconSrc} alt={label} width={16} height={16} />
          </div>
        </div>
        <span
          className='text-sm font-medium'
          style={{ color: '#44403c', lineHeight: 'normal' }}
        >
          {label}
        </span>
      </div>
    );
  }

  // Motorcycle pill
  return (
    <div style={commonPillStyle} className='flex flex-row'>
      <div
        className='relative flex-shrink-0'
        style={{
          width: '26.333px',
          height: '19.333px',
        }}
      >
        <div
          className='absolute inset-0 flex items-center justify-center'
          style={{
            backgroundColor: '#e7e5e4',
            borderRadius: '2px',
          }}
        >
          <Image src={iconSrc} alt={label} width={16} height={16} />
        </div>
      </div>
      <span
        className='text-sm font-medium text-right'
        style={{ color: '#44403c', lineHeight: 'normal' }}
      >
        {label}
      </span>
    </div>
  );
}
